// common/modals.js
import pageScrollState from '../lib/page-scroll-state';
import { bus as eventBus } from '../classes/PubSub';
import FocusableElements from '../classes/FocusableElements';

// Store opener element per modal
const openerByModal = new WeakMap();
// Cache focusables per modal (for trap)
const focusablesByModal = new WeakMap();
// Active modals counter for robust scroll locking
let activeCount = 0;

// Helpers
const getModalEl = (m) => (typeof m === 'string' ? document.getElementById(m) : m);
const getBgEl = (modal) => (modal?.dataset?.bgId ? document.getElementById(modal.dataset.bgId) : null);

function openModal(modal, openerEl) {
    modal = getModalEl(modal);
    if (!modal || modal.classList.contains('active')) return;

    openerByModal.set(modal, openerEl || document.activeElement || null);

    // Scroll lock (with depth)
    if (activeCount === 0) pageScrollState.fix();
    activeCount++;

    // Overlay on
    const bg = getBgEl(modal);
    if (bg) bg.classList.add('active');

    // A11y attributes
    if (!modal.hasAttribute('role')) modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-hidden', 'false');

    modal.classList.add('active');
    eventBus.emit('modalOpened', modal);

    // Focus first focusable
    const fe = new FocusableElements(modal);
    focusablesByModal.set(modal, fe);
    (fe.firstReal || fe.first || modal).focus();
}

function closeModal(modal) {
    modal = getModalEl(modal);
    if (!modal || !modal.classList.contains('active')) return;

    // Overlay off
    const bg = getBgEl(modal);
    if (bg) bg.classList.remove('active');

    modal.classList.remove('active');
    modal.setAttribute('aria-hidden', 'true');

    eventBus.emit('modalClosed', modal);

    // Restore focus to opener if present
    const opener = openerByModal.get(modal);
    if (opener && typeof opener.focus === 'function') {
        opener.focus();
    }
    openerByModal.delete(modal);
    focusablesByModal.delete(modal);

    // Scroll unlock (with depth)
    activeCount = Math.max(0, activeCount - 1);
    if (activeCount === 0) pageScrollState.unfix();
}

// Close all currently opened modals (helper)
function closeAllModals() {
    document.querySelectorAll('.modal.active').forEach((m) => closeModal(m));
}

// --- Event delegation: clicks ---
document.addEventListener('click', (e) => {
    // Openers
    const opener = e.target.closest('.modal-opener');
    if (opener) {
        e.preventDefault();
        closeAllModals();
        openModal(opener.dataset.modalId, opener);
        return;
    }

    // Any element marked as close trigger inside a modal
    const closeTrigger = e.target.closest('[data-modal-close], .close-btn');
    if (closeTrigger) {
        const modal = e.target.closest('.modal');
        if (modal) closeModal(modal);
        return;
    }

    // Click outside .window closes (when no separate bg)
    const modal = e.target.closest('.modal');
    if (modal && !e.target.closest('.window') && modal.id !== 'notice-pop-up' && !modal.dataset.bgId) {
        closeModal(modal);
        return;
    }

    // Separate background click (match by id)
    const bg = e.target.closest('.modal-bg, [id]');
    if (bg && bg.id) {
        const host = document.querySelector(`.modal[data-bg-id="${bg.id}"]`);
        if (host && host.id !== 'notice-pop-up') closeModal(host);
    }
}, { passive: true });

// --- Keyboard: Escape + Tab focus trap ---
document.addEventListener('keydown', (e) => {
    // Escape closes the top-most active modal
    if (e.key === 'Escape') {
        const opened = Array.from(document.querySelectorAll('.modal.active'));
        const top = opened[opened.length - 1];
        if (top && top.id !== 'notice-pop-up') {
            e.preventDefault();
            closeModal(top);
        }
        return;
    }

    // Trap focus on Tab/Shift+Tab
    if (e.key !== 'Tab') return;
    const modal = document.activeElement?.closest?.('.modal.active');
    if (!modal) return;

    const fe = focusablesByModal.get(modal) || new FocusableElements(modal);
    const first = fe.firstReal || fe.first;
    const last  = fe.lastReal || fe.last;
    if (!first || !last) return;

    if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
    }
});

export { openModal, closeModal, closeAllModals };
